import {
  IconHome,
  IconBriefcase,
  IconFileDescription,
  IconMessage,
  IconWorld,
  IconFolders,
  IconFlag,
  IconId,
  IconCategory,
  IconListCheck,
  IconFileStack,
  IconQuestionCircle,
  IconTimelineEvent,
  IconChartBar,
  IconDevices,
  IconAward,
  IconQuote,
  IconMapPin,
  IconUsers,
  IconShieldCheck,
  IconLock,
  IconHistory,
  IconSettings,
  IconBellRinging,
  IconRadar,
  IconCash,
  IconFileText,
} from "@tabler/icons";

import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import { RouterName } from "src/routes/RouterName";

// The menu, grouped by the job a staff member came to do. `privilege` is the
// key in the stored privileges (a section shows only when the person can
// read it); `match` lists the other paths that belong to an item, so it stays
// highlighted on its detail pages.
const SECTIONS = [
  {
    items: [
      { title: "Home", icon: IconHome, href: "/dashboard", privilege: "24" },
      { title: "Money", icon: IconCash, href: RouterName.money, privilege: "24" },
    ],
  },
  {
    subheader: "Applications",
    items: [
      { title: "Cases", icon: IconBriefcase, href: RouterName.cases, privilege: "23", match: ["/cases/"] },
      // Each traveller of each application as a row (1611-2); a row opens its case.
      { title: "Cases by traveller", icon: IconFileDescription, href: RouterName.requests, privilege: "23", match: ["/request"] },
      { title: "Support chats", icon: IconMessage, href: RouterName.support, privilege: "25" },
    ],
  },
  {
    subheader: "Catalogue",
    items: [
      {
        title: "Countries",
        icon: IconWorld,
        href: RouterName.countriesDetails.countries,
        privilege: "4",
        // A country's reviews and its document and question lists.
        match: ["/request-reviews", "/products"],
      },
      // What the rule watch found on the official pages; `badge` shows the
      // open count (SidebarItems).
      { title: "Rule changes", icon: IconRadar, href: RouterName.ruleChanges, privilege: "4", badge: "ruleChanges" },
      { title: "Groups", icon: IconFolders, href: RouterName.countriesDetails.groups, privilege: "5" },
      { title: "Visa types", icon: IconId, href: RouterName.countriesDetails.visaType, privilege: "9" },
      { title: "Nationalities", icon: IconFlag, href: RouterName.countriesDetails.nationality, privilege: "8" },
      {
        title: "Question categories",
        icon: IconCategory,
        href: RouterName.countriesDetails.QuestionsCategory,
        privilege: "10",
      },
      { title: "Questions", icon: IconListCheck, href: RouterName.countriesDetails.questionnaires, privilege: "6" },
      { title: "Documents", icon: IconFileStack, href: RouterName.countriesDetails.documents, privilege: "7" },
    ],
  },
  {
    // What the website shows today (checked against safrati-web dev:
    // FAQs page and visa pages, About page partners, footer contact).
    subheader: "Website content",
    items: [
      { title: "FAQs", icon: IconQuestionCircle, href: RouterName.faq, privilege: "21" },
      { title: "Supported by", icon: IconAward, href: RouterName.trustedBy, privilege: "13" },
      { title: "Contact details", icon: IconMapPin, href: RouterName.contact, privilege: "19" },
      { title: "Waitlist", icon: IconBellRinging, href: RouterName.waitlist, privilege: "19" },
      { title: "About and legal pages", icon: IconFileText, href: RouterName.websiteTexts, privilege: "18" },
    ],
  },
  {
    subheader: "People",
    items: [
      { title: "Customers", icon: IconUsers, href: RouterName.clients, privilege: "3", match: ["/user-ai-chat-sessions"] },
      { title: "Staff", icon: IconShieldCheck, href: RouterName.admins.admins, privilege: "1" },
      // Its own right since migration 026 (27); before it, Staff's (1).
      { title: "Roles and access", icon: IconLock, href: RouterName.admins.privileges, privilege: "27", fallback: "1" },
      { title: "Activity log", icon: IconHistory, href: RouterName.admins_logs.admins_logs, privilege: "12" },
    ],
  },
  {
    subheader: "Settings",
    items: [{ title: "Site settings", icon: IconSettings, href: RouterName.settings.settings, privilege: "18" }],
  },
  {
    // Content the CMS can edit but no website page shows (safrati-web dev
    // fetches these into its store and never renders them; the Testimonials
    // block exists but no page mounts it): pages of the old website. Folded
    // by default so the menu leads with what is live; nothing is removed.
    // Steps customers do see are each product's "Steps after submission".
    subheader: "Old website (not shown)",
    collapsible: true,
    items: [
      { title: "How it works", icon: IconTimelineEvent, href: RouterName.working_process, privilege: "16" },
      { title: "Key numbers", icon: IconChartBar, href: RouterName.stats, privilege: "17" },
      { title: "Services", icon: IconDevices, href: RouterName.services, privilege: "14" },
      { title: "Testimonials", icon: IconQuote, href: RouterName.testimonials, privilege: "15" },
    ],
  },
];

// Other words people type for each page, for the "Go to a page" finder
// (Ctrl K): "price" finds Countries, "passport" finds Nationalities.
const KEYWORDS = {
  "About and legal pages": "our mission terms privacy refunds legal about",
  Home: "dashboard overview today my day",
  Money: "money paid payment payments owed owe refund refunds fee fees revenue income",
  Cases: "case application applicant order payment paid",
  "Cases by traveller": "request requests traveller travellers enquiry inquiry lead callback",
  "Support chats": "support chat message conversation help inbox",
  Countries: "country destination price product visa embassy fee",
  "Rule changes": "rule rules change alert finding official page embassy watch requirement suspended declaration",
  Groups: "group region schengen",
  "Visa types": "visa type tourist business transit student work",
  Nationalities: "nationality passport citizen citizenship",
  "Question categories": "category categories section",
  Questions: "question questionnaire form field application form",
  Documents: "document file requirement paper upload",
  FAQs: "faq question answer help",
  "Supported by": "partner logo logos brand trusted by supporter",
  "Contact details": "contact address phone email office location map",
  Waitlist: "waitlist waiting list notify demand destination passport opens interest",
  Customers: "customer user client account sign in login",
  Staff: "staff admin team employee member user users account accounts sign in",
  "Roles and access": "role permission privilege access rights",
  "Activity log": "log history audit undo restore change",
  // The legal texts and Our mission moved to "About and legal pages" (hala-27).
  "Site settings": "settings setting site config social link footer pay later whatsapp currency rates exchange",
  "How it works": "process step steps working process refusal reasons",
  "Key numbers": "stats statistic number counter figure",
  Services: "service offer",
  Testimonials: "testimonial review quote feedback",
};

export const canRead = (privileges, key) => privileges?.[key]?.access_read == 1;

// The sections this person can see, without empty ones.
export function getMenuSections() {
  const privileges = getLocalStorage(dataLocalStorage.privileges) || {};
  return SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => canRead(privileges, privileges?.[item.privilege] || !item.fallback ? item.privilege : item.fallback)).map((item) => ({ ...item, id: item.href, keywords: KEYWORDS[item.title] || "" })),
  })).filter((section) => section.items.length > 0);
}

// Flat list in the template's shape ({ navlabel, subheader } rows followed by
// their items), for the code that still reads it that way.
export function getMenuItems() {
  return getMenuSections().flatMap((section) => [
    ...(section.subheader ? [{ navlabel: true, subheader: section.subheader }] : []),
    ...section.items,
  ]);
}

// How closely `path` sits under `base`, in whole segments: /cases/12 is under
// /cases, /request/5 is not under /requests. 0 when it is not under it.
function depthUnder(path, base) {
  const a = path.split("/").filter(Boolean);
  const b = String(base || "").split("/").filter(Boolean);
  if (!b.length || b.length > a.length) return 0;
  return b.every((part, i) => part === a[i]) ? b.length : 0;
}

// The one menu item that `pathname` belongs to: its own page or a detail page
// under it, or a page an item claims in `match`. The closest match wins.
// (A product's /products/3/documents is Countries' whether or not its country
// is in a group: the old /ListDocuments/group/3 redirects there.)
export function activeItemId(pathname) {
  let best = null;
  let bestDepth = 0;
  getMenuSections().forEach((section) =>
    section.items.forEach((item) => {
      const depth = Math.max(pathname === item.href ? Infinity : 0, depthUnder(pathname, item.href), ...(item.match || []).map((m) => depthUnder(pathname, m)));
      if (depth > bestDepth) {
        best = item.id;
        bestDepth = depth;
      }
    }),
  );
  return best;
}

// True when `pathname` is this item's page or one of its detail pages.
export function isActive(item, pathname) {
  return item.id === activeItemId(pathname);
}

// Where "/" sends someone: Home if they can see it, else their first page.
export function homePath() {
  const first = getMenuSections()[0]?.items[0];
  return first?.href || "/dashboard";
}

export default getMenuItems();
